import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { useApi } from "./useApi";
import type { CuitResponse } from "@repo/shared";

interface CuitContextValue {
  cuits: CuitResponse[];
  activeCuit: CuitResponse | null;
  setActiveCuitId: (id: number) => void;
  loading: boolean;
  refresh: () => Promise<void>;
}

export const CuitContext = createContext<CuitContextValue>({
  cuits: [],
  activeCuit: null,
  setActiveCuitId: () => {},
  loading: true,
  refresh: async () => {},
});

const ACTIVE_CUIT_STORAGE_KEY = "facturador.activeCuitId";

function getPersistedActiveCuitId(): number | null {
  if (typeof window === "undefined") return null;
  const rawValue = window.localStorage.getItem(ACTIVE_CUIT_STORAGE_KEY);
  if (!rawValue) return null;

  const parsed = Number(rawValue);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function persistActiveCuitId(id: number | null) {
  if (typeof window === "undefined") return;
  if (id === null) {
    window.localStorage.removeItem(ACTIVE_CUIT_STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(ACTIVE_CUIT_STORAGE_KEY, String(id));
}

export function useCuitProvider() {
  const { authFetch } = useApi();
  const [cuits, setCuits] = useState<CuitResponse[]>([]);
  const [activeCuitId, setActiveCuitIdState] = useState<number | null>(() =>
    getPersistedActiveCuitId(),
  );
  const [loading, setLoading] = useState(true);

  const setActiveCuitId = useCallback((id: number) => {
    setActiveCuitIdState(id);
    persistActiveCuitId(id);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = (await authFetch("/api/cuits")) as CuitResponse[];
      setCuits(data);
      if (data.length === 0) {
        setActiveCuitIdState(null);
        persistActiveCuitId(null);
        return;
      }

      setActiveCuitIdState((currentActiveId) => {
        const preferredId = currentActiveId ?? getPersistedActiveCuitId();
        const hasPreferredId =
          preferredId !== null && data.some((cuit) => cuit.id === preferredId);
        const nextActiveId = hasPreferredId ? preferredId : data[0].id;
        persistActiveCuitId(nextActiveId);
        return nextActiveId;
      });
    } catch (err) {
      console.error("Failed to load CUITs:", err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const activeCuit = cuits.find((c) => c.id === activeCuitId) ?? null;

  return {
    cuits,
    activeCuit,
    setActiveCuitId,
    loading,
    refresh,
  };
}

export function useCuits() {
  return useContext(CuitContext);
}
